import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import { PasswordRequirements, isPasswordValid } from '@/components/features/auth/password-requirements';

/**
 * Passwort-Reset-Seite — Zwei-Schritt-Flow.
 * Schritt 1: Email eingeben → Code anfordern
 * Schritt 2: Code + neues Passwort eingeben → Reset abschliessen
 *
 * @see Spec 0043 Section 4.2
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { resetPassword, confirmResetPassword } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t('forgotPassword.error.emailRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword(email.trim());
      // Generische Meldung — auch bei ungueltigem Email (Spec 0043: kein User-Leaking)
      setStep(2);
    } catch (err) {
      // Auch bei Fehlern generische Meldung (kein Leaking)
      if (err instanceof Error && (err.name === 'LimitExceededException' || err.name === 'TooManyRequestsException')) {
        setError(t('forgotPassword.error.tooManyRequests'));
      } else {
        // Trotzdem zu Step 2 wechseln — generische Meldung
        setStep(2);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError(t('forgotPassword.error.codeRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('forgotPassword.error.passwordMismatch'));
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError(t('forgotPassword.error.passwordNotAllRequirements'));
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmResetPassword(email.trim(), code.trim(), newPassword);
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'CodeMismatchException') {
          setError(t('forgotPassword.error.codeInvalid'));
        } else if (err.name === 'ExpiredCodeException') {
          setError(t('forgotPassword.error.codeExpired'));
        } else if (err.name === 'LimitExceededException' || err.name === 'TooManyRequestsException') {
          setError(t('forgotPassword.error.tooManyAttempts'));
        } else if (err.name === 'InvalidPasswordException') {
          setError(t('forgotPassword.error.passwordNotRequirements'));
        } else {
          setError(t('forgotPassword.error.resetFailed'));
        }
      } else {
        setError(t('forgotPassword.error.unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-6">
          <h1 className="mb-6 text-center text-lg font-semibold">
            {step === 1 ? t('forgotPassword.requestTitle') : t('forgotPassword.resetTitle')}
          </h1>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('forgotPassword.requestIntro')}
              </p>

              <div className="space-y-2">
                <Label htmlFor="email">{t('forgotPassword.emailLabel')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><Spinner size="sm" />{t('forgotPassword.requestSubmitting')}</span>
                ) : (
                  t('forgotPassword.requestSubmit')
                )}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950 dark:text-green-300">
                {t('forgotPassword.codeSentBanner')}
              </div>

              <p className="text-sm text-muted-foreground">
                {t('forgotPassword.resetIntro')}
              </p>

              <div className="space-y-2">
                <Label htmlFor="code">{t('forgotPassword.codeLabel')}</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="123456"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('forgotPassword.newPasswordLabel')}</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? t('common.hide') : t('common.show')}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('forgotPassword.confirmPasswordLabel')}</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <PasswordRequirements password={newPassword} />

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><Spinner size="sm" />{t('forgotPassword.resetSubmitting')}</span>
                ) : (
                  t('forgotPassword.resetSubmit')
                )}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
